/**
 * Shared payroll calculations — mirrors Payroll Queue / Payroll Report logic.
 */

import {
  computeEmployeePayrollTotals,
  computePayrollPeriodTotals,
  getEmployeePayrollKey,
  resolveWeeklyOvertimeSettings,
} from "./payrollOvertime";
import { getPayrollWeekBoundsFromSettings } from "./payrollWeek";

export {
  formatWeekRangeLabel,
  getPayrollWeekBounds,
  getPayrollWeekBoundsFromSettings,
  getSundayWeekBounds,
} from "./payrollWeek";

export {
  computeEmployeePayrollTotals,
  computePayrollPeriodTotals,
  computeRowBaseGrossPay,
  getEmployeePayrollKey,
  resolveWeeklyOvertimeSettings,
} from "./payrollOvertime";

/** Line-item base pay (hours × rate). Use computePayrollPeriodTotals for OT-adjusted gross. */
export function computeRowGrossPay(row) {
  const hours = Number(row?.total_hours || 0);
  const rate = Number(row?.hourly_rate || 0);
  return hours * rate;
}

export function sumGrossPay(rows, orgSettings) {
  if (orgSettings) {
    return computePayrollPeriodTotals(rows, orgSettings).grossPay;
  }

  return (rows || []).reduce(
    (sum, row) => sum + computeRowGrossPay(row),
    0,
  );
}

/**
 * OT-aware liability for unpaid rows that may span multiple payroll weeks.
 * Partitions by employee + payroll week, then aggregates via payrollOvertime.
 */
export function computePayrollLiabilityTotals(rows = [], orgSettings) {
  const overtimeSettings = resolveWeeklyOvertimeSettings(orgSettings);
  const periodGroups = new Map();

  (rows || []).forEach((row) => {
    if (!row?.date_iso) {
      return;
    }

    const employeeKey = getEmployeePayrollKey(row);
    const { weekStartStr } = getPayrollWeekBoundsFromSettings(
      new Date(`${row.date_iso}T12:00:00`),
      orgSettings,
    );
    const groupKey = `${employeeKey}|${weekStartStr}`;
    const existing = periodGroups.get(groupKey) || [];
    existing.push(row);
    periodGroups.set(groupKey, existing);
  });

  const employeeAmounts = new Map();
  let totalHours = 0;
  let regularHours = 0;
  let overtimeHours = 0;
  let grossPay = 0;

  periodGroups.forEach((groupRows) => {
    const totals = computeEmployeePayrollTotals(groupRows, overtimeSettings);
    const employeeName = groupRows[0]?.employee || "Unknown Employee";

    employeeAmounts.set(
      employeeName,
      (employeeAmounts.get(employeeName) || 0) + totals.grossPay,
    );

    totalHours += totals.totalHours;
    regularHours += totals.regularHours;
    overtimeHours += totals.overtimeHours;
    grossPay += totals.grossPay;
  });

  const byEmployee = Array.from(employeeAmounts.entries())
    .map(([name, amount]) => ({ name, amount }))
    .sort((left, right) => right.amount - left.amount);

  return {
    grossPay,
    totalHours,
    regularHours,
    overtimeHours,
    byEmployee,
  };
}

export function sumHours(rows) {
  return (rows || []).reduce(
    (sum, row) => sum + Number(row?.total_hours || 0),
    0,
  );
}

/**
 * Editor / clock-in payroll type choices.
 * Stored keys stay Standard|Residential|Commercial|Industrial|Custom.
 * Rate resolution (see payrollRateResolution.js):
 * - Standard (User Rate) → users.hourly_rate
 * - Residential (CCQ) → payroll_rates Residential
 * - Custom (Non-CCQ) → same Residential matrix rate; key kept for reporting
 * - Commercial / Industrial → payroll_rates for that type
 */
export const TIME_LOG_PAYROLL_TYPE_OPTIONS = Object.freeze([
  { value: "Standard", label: "User Rate" },
  { value: "Residential", label: "Residential (CCQ)" },
  { value: "Commercial", label: "Commercial" },
  { value: "Industrial", label: "Industrial" },
  { value: "Custom", label: "Residential (Non-CCQ)" },
]);

/** User-facing payroll type labels (matches session / time-log editor UI). */
export const PAYROLL_TYPE_DISPLAY_LABELS = Object.freeze({
  Standard: "User Rate",
  Residential: "Residential (CCQ)",
  Commercial: "Commercial",
  Industrial: "Industrial",
  Custom: "Residential (Non-CCQ)",
});

export function formatPayrollTypeLabel(payrollType) {
  if (payrollType == null || payrollType === "") {
    return "—";
  }

  return PAYROLL_TYPE_DISPLAY_LABELS[payrollType] ?? payrollType;
}

/**
 * Resolve a UI label for a payroll type.
 * - Prefer organization display_label when it is a real label (not the raw system key).
 * - When catalog still stores legacy labels equal to type_key (e.g. "Standard"),
 *   use the approved V2 map so Start Session never shows engine keys.
 */
export function resolvePayrollTypeDisplayLabel(typeKey, displayLabel = null) {
  if (typeKey == null || typeKey === "") {
    return "—";
  }

  const trimmed = String(displayLabel ?? "").trim();
  if (trimmed && trimmed !== typeKey) {
    return trimmed;
  }

  return formatPayrollTypeLabel(typeKey);
}
